import DOMPurify from "dompurify";

export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export const tailwindBgToHex = {
  "bg-red-400": "#f87171",
  "bg-orange-400": "#fb923c",
  "bg-indigo-400": "#818cf8",
  "bg-purple-400": "#a78bfa",
  "bg-green-400": "#4ade80",
  "bg-yellow-400": "#facc15",
  "bg-blue-400": "#60a5fa",
  "bg-pink-400": "#f472b6",
  "bg-gray-400": "#9ca3af",
  "bg-cyan-500": "#06b6d4",
  "bg-teal-400": "#2dd4bf",
  "bg-emerald-400": "#34d399",
  "bg-lime-400": "#a3e635",
  "bg-amber-600": "#d97706",
  "bg-rose-400": "#fb7185",
  "bg-violet-400": "#c084fc",
  "bg-sky-400": "#38bdf8",
  "bg-fuchsia-500": "#d946ef",
  "bg-zinc-400": "#a1a1aa",
  "bg-gray-500": "#6b7280",
};

export const getHexColor = (twClass) => tailwindBgToHex[twClass] || "#f27131";

export function clamp(number, min, max) {
  return Math.max(min, Math.min(max, number));
}

export function hexToRgb(hex) {
  const cleanHex = String(hex || "#f27131").replace("#", "").trim();

  const fullHex =
    cleanHex.length === 3
      ? cleanHex
          .split("")
          .map((value) => value + value)
          .join("")
      : cleanHex;

  return {
    r: parseInt(fullHex.slice(0, 2), 16),
    g: parseInt(fullHex.slice(2, 4), 16),
    b: parseInt(fullHex.slice(4, 6), 16),
  };
}

export function rgbToHex({ r, g, b }) {
  const toHex = (value) => value.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function lighten(hex, amount = 0.6) {
  const { r, g, b } = hexToRgb(hex);

  return rgbToHex({
    r: Math.round(r + (255 - r) * amount),
    g: Math.round(g + (255 - g) * amount),
    b: Math.round(b + (255 - b) * amount),
  });
}

export function isDark(hex) {
  const { r, g, b } = hexToRgb(hex);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance < 0.55;
}

export function toAbsolute(url) {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (
    /^https?:\/\//i.test(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

  if (cleanUrl.startsWith("/")) {
    return `${baseUrl}${cleanUrl}`;
  }

  return `${baseUrl}/${cleanUrl}`;
}

export function teachersOf(group) {
  return [
    group?.enseignant1,
    group?.enseignant2,
    group?.enseignant3,
    group?.enseignant4,
    group?.enseignant5,
    group?.enseignant6,
  ]
    .filter(Boolean)
    .map((teacher) => String(teacher).trim())
    .filter(Boolean);
}

export function sanitizeHtml(html) {
  return DOMPurify.sanitize(html || "", {
    USE_PROFILES: {
      html: true,
    },
    FORBID_ATTR: ["style"],
  });
}

export function normalizeDiscipline(value) {
  return String(value || "").trim().toLowerCase();
}

export function extractDisciplinesUnique(groups) {
  const disciplines = new Set();

  (groups || []).forEach((group) => {
    const discipline = String(group?.discipline_groupe || "").trim();
    if (discipline) disciplines.add(discipline);
  });

  return Array.from(disciplines).sort((a, b) => a.localeCompare(b, "fr"));
}

export function extractAnneesUnique(groups) {
  const years = new Set();

  (groups || []).forEach((group) => {
    const rawYears = group?.annees_naissance;

    if (!rawYears) return;

    String(rawYears)
      .split("/")
      .forEach((fragment) => {
        const cleanFragment = fragment.trim();

        if (/^(\d{4}-)+\d{4}$/.test(cleanFragment)) {
          cleanFragment.split("-").forEach((year) => {
            if (/^\d{4}$/.test(year)) years.add(year);
          });
          return;
        }

        if (/^\d{4}$/.test(cleanFragment)) {
          years.add(cleanFragment);
          return;
        }

        if (/^\d{4}-\d{4}$/.test(cleanFragment)) {
          const [firstYear, secondYear] = cleanFragment.split("-");
          years.add(firstYear);
          years.add(secondYear);
          return;
        }

        if (/^\+?\d+\s?ans$/i.test(cleanFragment)) {
          const value = cleanFragment.match(/\d+/)?.[0];
          if (value) years.add(`+${value} ans`);
        }
      });
  });

  return Array.from(years).sort((a, b) => {
    const numberA = parseInt(a, 10);
    const numberB = parseInt(b, 10);

    if (!Number.isNaN(numberA) && !Number.isNaN(numberB)) {
      return numberB - numberA;
    }

    return a.localeCompare(b, "fr");
  });
}

export function matchAnnee(anneesNaissance, selectedAnnee) {
  if (!selectedAnnee) return true;

  const field = String(anneesNaissance || "");
  const normalizedField = field.replace(/\s+/g, "");
  const normalizedSelected = String(selectedAnnee)
    .replace(/\s+/g, "")
    .replace(/ans$/i, "ans");

  const fragments = normalizedField
    .split("/")
    .map((fragment) => fragment.trim())
    .map((fragment) => {
      if (/^\d{4}-\d{4}$/.test(fragment)) return fragment.split("-");

      if (/^\+?\d+ans$/i.test(fragment)) {
        const value = fragment.match(/\d+/)?.[0];
        return `+${value}ans`;
      }

      return fragment;
    })
    .flat()
    .filter(Boolean);

  return fragments.some((fragment) => fragment.includes(normalizedSelected));
}

export const ORDERED_WEEK_DAYS = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];

export function extractJoursFromGroupes(groups) {
  const days = new Set();

  (groups || []).forEach((group) => {
    for (let index = 1; index <= 6; index += 1) {
      const horaire = group[`horaire${index}`];

      if (horaire) {
        const [day] = String(horaire).split(" ");
        if (day) days.add(day);
      }
    }
  });

  return Array.from(days).sort(
    (a, b) => ORDERED_WEEK_DAYS.indexOf(a) - ORDERED_WEEK_DAYS.indexOf(b)
  );
}

export function horairesOf(group) {
  return Array.from({ length: 6 })
    .map((_, index) => group?.[`horaire${index + 1}`])
    .filter(Boolean);
}

export function totalTarif(group) {
  return (
    (parseFloat(group?.tarif_licence || 0) || 0) +
      (parseFloat(group?.tarif_adhesion || 0) || 0) +
    (parseFloat(group?.tarif_cotisation || 0) || 0)
  );
}

export function fillPercentage(group) {
  if (!group?.quota_groupe || Number(group.quota_groupe) <= 0) return 0;

  return Math.round(
    ((Number(group.adherentCount) || 0) / Number(group.quota_groupe)) * 100
  );
}

export function isGroupFull(group) {
  return (
    Number(group?.quota_groupe) > 0 &&
    Number(group?.adherentCount || 0) >= Number(group?.quota_groupe || 0)
  );
}